/**
 * features/customers/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerInput } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Paginated, Party } from '~/lib/types';

export interface CustomerListParams {
  page: number;
  pageSize: number;
  search: string;
}

export function useCustomers(params: CustomerListParams) {
  return useQuery({
    queryKey: queryKeys.customers(params),
    queryFn: () =>
      api.get<Paginated<Party>>(
        `/api/customers${queryString({ page: params.page, pageSize: params.pageSize, search: params.search })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerInput) => api.post<Party>('/api/customers', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CustomerInput }) =>
      api.put<Party>(`/api/customers/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/customers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
